import { useState } from "react";
import type { LookUpEnvelope } from "../../../edge/src/contracts";

interface Props {
  envelope: LookUpEnvelope;
  onOpenStory?: () => void;
}

/**
 * Look Up view. Renders mediaPreview only on first paint (FR-014, P3). When
 * `mediaFull` is present and the user taps the preview, we swap to the full
 * image and mark the element with `data-media="full"` (the gate-A test
 * asserts count(0) of `[data-media="full"]` on first paint, never after a
 * deliberate user tap).
 */
export function LookUpView({ envelope, onOpenStory }: Props) {
  const r = envelope.result;
  const [expanded, setExpanded] = useState(false);
  const canExpand = Boolean(r.mediaFull && r.mediaPreview.kind === "image");
  return (
    <article
      className="lookup"
      lang="en"
      aria-busy={envelope.kind === "fallback"}
      data-testid="lookup-card"
      data-module-id={r.moduleId}
    >
      <header>
        <h1>{r.title}</h1>
        <span
          data-test="lookup-diagnostics"
          data-cache={envelope.diagnostics.degraded ? "degraded" : envelope.diagnostics.cache}
          data-latency={envelope.diagnostics.latencyMs}
          hidden
        />
        {envelope.kind === "fallback" && (
          <p className="fallback-banner" role="status">
            Generated fallback — we are still verifying sources for this query.
          </p>
        )}
        {envelope.diagnostics.degraded && (
          <p className="degraded-banner" role="status">
            Showing a slightly older answer while we refresh.
          </p>
        )}
      </header>
      {r.mediaPreview.kind === "image" ? (
        expanded && r.mediaFull ? (
          <button
            type="button"
            className="media-expanded"
            onClick={() => setExpanded(false)}
            aria-label="Close expanded media"
          >
            <img
              src={r.mediaFull.url}
              alt={r.mediaFull.alt ?? r.mediaPreview.alt}
              data-media="full"
              decoding="async"
              width={r.mediaFull.width ?? undefined}
              height={r.mediaFull.height ?? undefined}
            />
          </button>
        ) : (
          <button
            type="button"
            className="media-preview"
            onClick={() => canExpand && setExpanded(true)}
            aria-label={canExpand ? "Tap to expand media" : r.mediaPreview.alt}
            disabled={!canExpand}
          >
            <img
              src={r.mediaPreview.url}
              alt={r.mediaPreview.alt}
              loading="lazy"
              decoding="async"
              width={320}
              height={320}
            />
          </button>
        )
      ) : (
        <div className="typographic" aria-label={r.mediaPreview.alt}>
          {r.title.slice(0, 1)}
        </div>
      )}
      <p className="summary">{r.summary}</p>
      <footer className="actions">
        {r.sourceUrl && (
          <a href={r.sourceUrl} target="_blank" rel="noreferrer noopener">
            Open original page
          </a>
        )}
        {onOpenStory && envelope.kind === "answer" && (
          <button type="button" onClick={onOpenStory} aria-label="Open Story">
            Open Story
          </button>
        )}
      </footer>
    </article>
  );
}
